// services/registerUser.ts
import { auth, db } from "../firebaseConfig";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { NavigateFunction } from "react-router-dom";
import { UserData } from "../types/User";

export async function registerUserAccount(
  email: string,
  password: string,
  additionalData: Omit<UserData, "createdAt" | "email">,
): Promise<{ success: boolean; message?: string }> {
  try {
    // Perform user registration logic
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const user = userCredential.user;

    // Send email verification
    await sendEmailVerification(user);

    const userData: UserData = {
      email,
      ...additionalData,
      createdAt: new Date().toISOString(),
    };

    await setDoc(doc(db, "users", user.uid), userData);

    return { success: true };
  } catch (error: any) {
    console.error("Error registering user:", error.message);
    return { success: false, message: error.message };
  }
}

export async function loginUserAccount(
  email: string,
  password: string,
): Promise<{
  success: boolean;
  message?: string;
  data?: object;
  userId?: string;
}> {
  try {
    // Firebase login
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );

    const user = userCredential.user;

    if (!user.emailVerified) {
      console.error("Email not verified. Please check your inbox.");
      return {
        success: false,
        message: "Please verify your email",
      };
    }

    //Get additional user info
    const userDoc = await getDoc(doc(db, "users", user.uid));

    return {
      success: true,
      message: "Login success",
      data: userDoc.data(),
      userId: user?.uid,
    };
  } catch (error: any) {
    console.error("Login error:", error.message);
    return { success: false, message: error.message };
  }
}

export async function resetPassword(
  email: string,
): Promise<{ success: boolean; message: string }> {
  try {
    await sendPasswordResetEmail(auth, email);
    return {
      success: true,
      message: "Password reset email sent successfully.",
    };
  } catch (error: any) {
    console.error("Error resetting password:", error.message);
    return { success: false, message: error.message };
  }
}

export async function confirmPwdReset(
  oobCode: string,
  newPassword: string,
): Promise<{ success: boolean; message: string }> {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
    return {
      success: true,
      message: "Password was changed successfully.",
    };
  } catch (error: any) {
    console.error("Error changing password:", error.message);
    return { success: false, message: error.message };
  }
}
